/**
 * Доля ядра публичного рендерера: вид `redirect_url` (блок «Редирект»).
 *
 * WEB-2921, заход 1: компонент `RedirectBlock` перенесён из
 * `PublicBlockRenderer.tsx` слово в слово (у блока свои хуки — отсчёт и уход,
 * поэтому это компонент, а не функция отрисовки). Метка `case 'redirect_url'`
 * и вызов компонента остаются в ядре. Доля едет в первом экране вместе с
 * ядром (статический импорт).
 */
import { useEffect, useState } from 'react';
import { usePageShown } from '@/public/useAppShownSignal';
import { safeHref } from '@/utils/isAllowedUrl';
import { useEditorPreview } from '../EditorPreviewContext';
import { useExternalExit } from '../externalExitContext';
import { AuthorHintBox } from '../AuthorHintBox';
import { pickStrings } from '@/i18n';
// WEB-2057: подсказки АВТОРУ — из своего маленького словаря. Полный словарь
// конструктора этому файлу нельзя: его рисует ПУБЛИЧНАЯ страница покупателя.
import { authorHintsStrings } from '@/i18n/dictionaries/authorHints';
import type { PublicQuizStrings } from '@/utils/publicQuizStrings';

interface RedirectBlockProps {
  /** WEB-2164: идентификатор цели для серверной точки ухода. */
  blockId: string;
  url?: string;
  delaySeconds?: number;
  /** WEB-681 QA: показывать ли URL в заглушке превью (default false —
      длинные ссылки уродовали блок и вылезали за границу). */
  showUrl?: boolean;
  /**
   * WEB-2967: надписи покупателю — из словаря публичной воронки по языку
   * воронки (ядро передаёт свой `publicStrings`), как у остальных веток ядра.
   */
  strings: Pick<PublicQuizStrings, 'redirectingIn' | 'redirectFallbackLink'>;
}

export function RedirectBlock({
  blockId,
  url,
  delaySeconds = 3,
  showUrl,
  strings,
}: RedirectBlockProps) {
  // WEB-681: inside the dashboard editor's phone preview, redirect_url must
  // never actually navigate — it used to run the exact same timer as the
  // public player and yanked the editor's own tab to the target URL.
  const { isEditorPreview } = useEditorPreview();
  // WEB-2124: единая дверь ухода плеера квиза. `null` вне плеера (канва
  // конструктора, пейволл) — тогда блок уводит сам, как раньше.
  const externalExit = useExternalExit();
  const [remaining, setRemaining] = useState(delaySeconds);
  // WEB-3673: на прогретой SDK странице (`?preload=1`) отсчёт до ухода идёт
  // от показа человеку, а не от фоновой загрузки.
  const shown = usePageShown();

  useEffect(() => {
    if (!url || isEditorPreview || !shown) return;
    if (remaining <= 0) {
      // WEB-173 F9: only navigate to an allowed scheme (defense-in-depth).
      //
      // WEB-1911: та же ДВЕРЬ, что у остальных адресных веток плеера, —
      // `safeHref`, а не общий предикат `isAllowedUrl`. Разница ровно в
      // маркере `w2a://store`: allowlist пропускает его как допустимое
      // ЗНАЧЕНИЕ поля, но адресом он быть не может (подменять маркер умеет
      // одна ветка `button_link`), и покупатель уезжал бы на несуществующую
      // схему молча. Пустое запасное значение = перехода нет.
      const target = safeHref(url, '');
      if (target !== '') {
        // WEB-2124: до этой правки здесь стоял голый переход — событие
        // завершения не излучалось, а всё, что накопилось в очереди событий,
        // терялось вместе с выгрузкой страницы. Порядок «событие → досыл →
        // уход» живёт в одной двери (externalExitContext), а не переписан
        // здесь второй копией.
        if (externalExit) {
          externalExit.exitViaRedirectBlock(blockId, target);
        } else {
          window.location.href = target;
        }
      }
      return;
    }
    const timer = setTimeout(() => setRemaining((r) => r - 1), 1000);
    return () => clearTimeout(timer);
  }, [remaining, url, isEditorPreview, shown, externalExit, blockId]);

  if (!url) return null;

  if (isEditorPreview) {
    return (
      <AuthorHintBox
        testId="redirect-block-editor-preview"
        style={{
          fontSize: undefined,
          background: 'var(--color-bg-surface, #fafafa)',
          color:
            'var(--color-text-on-surface, var(--color-text-primary, inherit))',
        }}
      >
        <div style={{ fontSize: 13, fontWeight: 600 }}>
          {pickStrings(authorHintsStrings).redirectBlockTitle}
        </div>
        {showUrl && (
          <div
            data-testid="redirect-preview-url"
            style={{
              fontSize: 11,
              // Ревизия пачки 26 (Ф1): правило on-surface (WEB-1911) довели
              // до самой плашки, но не до строк ВНУТРИ неё. У тёмной темы
              // кабинета --color-text-secondary СВЕТЛАЯ, а плашка стоит на
              // светлой поверхности воронки — адрес пропадал.
              color: 'var(--color-text-on-surface, rgba(0, 0, 0, 0.65))',
              marginTop: 4,
              wordBreak: 'break-all',
              maxWidth: '100%',
            }}
          >
            {url}
          </div>
        )}
        <div
          style={{
            fontSize: 12,
            // Ревизия пачки 26 (Ф1): см. строку адреса выше — тот же класс.
            color: 'var(--color-text-on-surface, rgba(0, 0, 0, 0.65))',
            marginTop: 4,
          }}
        >
          {pickStrings(authorHintsStrings).redirectAutoJump(delaySeconds)}
        </div>
      </AuthorHintBox>
    );
  }

  return (
    <div
      style={{
        textAlign: 'center',
        padding: 16,
        marginBottom: 16,
        background: 'var(--color-bg-surface, #fafafa)',
        border: '1px solid var(--color-border-primary, transparent)',
        borderRadius: 'var(--border-radius-md, 8px)',
        color:
          'var(--color-text-on-surface, var(--color-text-primary, inherit))',
      }}
    >
      <div
        style={{ fontSize: 14, color: 'var(--color-text-secondary, #595959)' }}
      >
        {strings.redirectingIn(remaining)}
      </div>
      <a
        href={safeHref(url)}
        style={{
          fontSize: 12,
          color: 'var(--color-primary, #1677ff)',
          display: 'block',
          marginTop: 4,
        }}
      >
        {strings.redirectFallbackLink}
      </a>
    </div>
  );
}
